import { api } from "@/lib/api-client";
import _ from "lodash";
import { generateLogTitle, generateDescription } from "./logFormatters";
import { Trans } from "@lingui/react/macro";
import { LimeTimeline } from "@/Components/LimeTimeline";
import { t } from "@lingui/core/macro";
import { Spinner } from "@heroui/react";
import type { FeatureId } from "@/server-types";
import { useClientFeature } from "@/hooks/useClientFeature";
import {
  getNotificationOffsetLabel,
  getNotificationSettingLabel,
} from "@/Components/AppointmentModal/components/NotificationSettingsSwitchList";

const CONTACT_CUSTOMER_FEATURE_ID: FeatureId = "contactCustomer";

const AppointmentLog = ({ appointmentId }: { appointmentId: number }) => {
  const { isFeatureEnabled } = useClientFeature();
  const isContactCustomerFeatureEnabled = isFeatureEnabled(
    CONTACT_CUSTOMER_FEATURE_ID,
  );
  const { data, isLoading: isLoadingLogs } =
    api.appointments.useAppointmentLog(appointmentId);
  const { data: notificationSettings } =
    api.notification.useGetNotificationSettings();

  const notificationSettingLabelById = Object.fromEntries(
    (notificationSettings?.settings ?? []).map((setting) => {
      const offset = getNotificationOffsetLabel(setting);
      const label = getNotificationSettingLabel(setting);
      return [setting.id, offset ? `${label} (${offset})` : label];
    }),
  );

  const uniqueLogs = _.uniqBy(
    data?.logs.map((log) => ({
      action: log.action,
      title: generateLogTitle(log),
      description: generateDescription(log, {
        includeContactCustomer: isContactCustomerFeatureEnabled,
        notificationSettingLabelById,
      }),
      timestamp: log.timestampLocalized,
      user: log.user,
    })),
    (log) => `${log.title}|${log.description}|${log.timestamp}`,
  );

  if (isLoadingLogs) {
    return <Spinner />;
  }

  if (!uniqueLogs?.length) {
    return (
      <p className="mt-4 text-center text-gray-400">
        <Trans>Zgodovina termina ni na voljo.</Trans>
      </p>
    );
  }

  return (
    <LimeTimeline
      items={uniqueLogs.map((log) => ({
        title: log.title,
        description: log.description,
        timestamp: log.timestamp,
        user:
          log.action === "issue_invoice"
            ? undefined
            : log.user
              ? {
                  fullName: `${log.user.name} ${log.user.lastName}`.trim(),
                }
              : {
                  fullName: t`Stranka`,
                },
      }))}
    />
  );
};

export default AppointmentLog;
